import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";

export interface AdminUserRow {
  id: number;
  email: string;
  created_at: string;
  last_login_at: string | null;
  approved: boolean;
  telegram_paired: boolean;
  live: number;
  tracked: number;
  idle: number;
  reanalyze_used_today: number;
}
export interface AdminUsersOut {
  rows: AdminUserRow[];
}

export interface AdminInviteRow {
  email: string;
  invited_at: string;
  used_at: string | null;
  invited_by: number | null;
  invited_by_email: string | null;
}
export interface AdminInvitesOut {
  rows: AdminInviteRow[];
}

const USERS_KEY = ["admin", "users"] as const;
const INVITES_KEY = ["admin", "invites"] as const;

export function useAdminUsers(enabled = true) {
  return useQuery<AdminUsersOut>({
    queryKey: USERS_KEY,
    queryFn: async () => (await api.get<AdminUsersOut>("/admin/users")).data,
    staleTime: 30_000,
    enabled,
  });
}

export function useAdminInvites(enabled = true) {
  return useQuery<AdminInvitesOut>({
    queryKey: INVITES_KEY,
    queryFn: async () => (await api.get<AdminInvitesOut>("/admin/invites")).data,
    staleTime: 30_000,
    enabled,
  });
}

export function useIssueInvite() {
  const qc = useQueryClient();
  return useMutation<AdminInviteRow, Error, string>({
    mutationFn: async (email) =>
      (await api.post<AdminInviteRow>("/admin/invites", { email })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INVITES_KEY });
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (email) => {
      await api.delete(`/admin/invites/${encodeURIComponent(email)}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INVITES_KEY });
    },
  });
}

export function useSetUserApproved() {
  const qc = useQueryClient();
  return useMutation<AdminUserRow, Error, { id: number; approved: boolean }>({
    mutationFn: async ({ id, approved }) =>
      (await api.patch<AdminUserRow>(`/admin/users/${id}`, { approved })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}
